import React from 'react';

import type { RequestExceptionWithResponse } from '@eika-infrastruktur/request';
import { Button } from '@eika/button';
import { CheckmarkAnimation } from '@eika/checkmark-animation';
import { Loader } from '@eika/loader';
import { RichIcon, RichIconTypes } from '@eika/rich-icon';

import { AccountsListContent, SettingsAccountOrderNeedMoreAccounts } from '..';
import ErrorCard from '../../../../../../components/errorcard/ErrorCard';
import CenteredModalContent from '../../../../../../components/shared/CenteredModalContent/CenteredModalContent';
import { useConfig } from '../../../../../../defaultConfig';
import { accountListSelector, useAccounts, useUpdateOrder } from '../../../../../../fetchHooks/useAccount';
import { useAccountsGroupsQuery } from '../../../../../../fetchHooks/useAccountsGroups';
import { useAccountsPropertiesQuery } from '../../../../../../fetchHooks/useAccountsProperties';
import { useCreditcards } from '../../../../../../fetchHooks/useCards';
import useTranslation from '../../../../../../hooks/useTranslation';
import mapAllAccountCardItems from '../../mapAccountCardItems';
import { DndContextProvider } from '../GroupedSortableDnd';

interface Props {
    onCompleted: () => void;
    isCompleted: boolean;
}

export const AccountsListQuery: React.FC<Props> = ({ onCompleted, isCompleted }) => {
    const { featureToggles } = useConfig();
    const translated = useTranslation();
    const { data: accounts, ...accountState } = useAccounts({ select: accountListSelector });
    const accountNumbers = accounts?.map((account) => account.accountNumber.value) ?? [];
    const { data: cards, ...cardsState } = useCreditcards(accountNumbers, featureToggles.enableCreditCardsFrontpage);

    const groupsQuery = useAccountsGroupsQuery();
    const propertiesQuery = useAccountsPropertiesQuery();

    const updateAccountOrder = useUpdateOrder();
    const accountCardItems = mapAllAccountCardItems(accounts, cards);

    if (updateAccountOrder.error) {
        return (
            <ErrorCard
                exception={updateAccountOrder.error as RequestExceptionWithResponse}
                title={translated.errors.somethingWentWrong}
                description={translated.editAccounts.errorNotSaved}
                icon={RichIconTypes.DATAFEIL_3}
                isModal
            >
                <Button onClick={onCompleted}>{translated.ok}</Button>
            </ErrorCard>
        );
    }

    if (accountState.error) {
        return (
            <ErrorCard
                exception={accountState.error}
                title={translated.errors.somethingWentWrong}
                description={translated.editAccounts.accountsError}
                icon={<RichIcon type={RichIconTypes.DATAFEIL_1} ariaLabel={translated.editAccounts.technicalError} />}
                isModal
            >
                <Button onClick={() => accountState.refetch()}>{translated.tryAgain}</Button>
            </ErrorCard>
        );
    }

    if (isCompleted) {
        return (
            <CenteredModalContent withMessage>
                <CheckmarkAnimation />
                <h3>{translated.editAccounts.success}</h3>
            </CenteredModalContent>
        );
    }

    if (accountState.status === 'pending') {
        return (
            <div className="action-result-centered">
                <Loader />
            </div>
        );
    }

    if (accountCardItems.length < 2) {
        return <SettingsAccountOrderNeedMoreAccounts okButton={onCompleted} />;
    }

    const cardsLoaded = featureToggles.enableCreditCardsFrontpage ? cardsState.status !== 'pending' : true;

    if (
        accountState.status === 'success' &&
        cardsLoaded &&
        !!accounts &&
        groupsQuery.status === 'success' &&
        groupsQuery.data !== undefined &&
        propertiesQuery.status === 'success' &&
        propertiesQuery.data !== undefined
    ) {
        return (
            <DndContextProvider
                cards={featureToggles.enableCreditCardsFrontpage ? cards : undefined}
                accounts={accounts}
                groups={groupsQuery.data}
                properties={propertiesQuery.data}
            >
                <AccountsListContent onCompleted={onCompleted} />
            </DndContextProvider>
        );
    }

    return (
        <div className="action-result-centered">
            <Loader />
        </div>
    );
};
